import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TableCellDisplay } from '@/components/ui-v2/inputs/table/table-integer-display';
import { TableIntegerInput } from '@/components/ui-v2/inputs/table/table-integer-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import {
  cn,
  getCurrentValue,
  getDeltaSign,
  getDeltaValue,
  getMyLastValue,
  hasPreviousVersion,
} from '@/lib/utils';
import { rangeFilterFn } from '@/lib/utils/table-filter-utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { evolutionInsightsState } from '@/states/evolution-insights.state';
import { kpiComparisonState } from '@/states/kpi-comparison.state';
import { KPIComparison } from '@/types/kpi-comparison';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface StockQuantityCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function StockQuantityCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: StockQuantityCellContentProps) {
  const { t } = useTranslation();
  const kpiComparison = useAtomValue(kpiComparisonState);

  const evolutionInsights = useAtomValue(evolutionInsightsState);
  const user = useUser();
  const isOfferLocked = useIsOfferLocked();
  const isEditing = useIsCellEditing(row, 'stockQuantity');
  const isParent = row.subRows.length > 1;

  const getValue = () => {
    const value = row.original.activeVersion.stockQuantity;
    if (typeof value === 'undefined' || value === null) return null;
    return value;
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.stockQuantity;
      if (typeof value !== 'undefined' && value !== null) {
        subRowsValues.push(value);
      }
    });
    if (subRowsValues.length === 0) return null;
    return subRowsValues.reduce((acc, val) => acc + val, 0);
  };

  const errorState = getTableCellErrorState({
    row,
    field: 'stockQuantity',
    errorType: 'none',
  });

  const displayValue = isParent ? getParentValue() : getValue();

  const getPreviousItem = () =>
    evolutionInsights?.campaignItemsWithEvolution.find(
      (item) => item.dealItemId === row.original.id
    )?.stockQuantity;

  const displayText =
    typeof displayValue === 'number'
      ? `${displayValue.toString()}${kpiComparison === KPIComparison.STOCK && getPreviousItem() ? ` (${Math.round(100 * (displayValue / (getPreviousItem() || 0) - 1)).toString()}%)` : ''}`
      : displayValue;

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="stockQuantity"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div
          className="flex h-full w-full flex-row"
          data-user-guide="Stock Quantity"
        >
          <div className="flex h-full w-full items-center">
            {isEditing ? (
              <TableIntegerInput
                value={row.original.activeVersion.stockQuantity ?? null}
                onChange={async (value) => {
                  await handleCellEdit(
                    row.original.activeVersion.id,
                    'stockQuantity',
                    value,
                    !isParent
                  );
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isOfferLocked}
                autoFocus={isEditing}
                hasError={errorState.hasError}
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-full flex-1">
                    <TableCellDisplay
                      value={displayText}
                      isEmpty={displayValue === null}
                      hasError={errorState.hasError}
                      errorMessage={errorState.errorMessage}
                      align="right"
                      isEditable={!isOfferLocked}
                      isParent={isParent}
                      isRecentlyModified={isRecentlyModified}
                      onClick={() =>
                        !isParent &&
                        handleCellClick(
                          row.original.activeVersion.id,
                          'stockQuantity'
                        )
                      }
                    >
                      {!isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'stockQuantity',
                          user.role
                        ) &&
                        getDeltaSign(
                          row.original,
                          'stockQuantity',
                          user?.role
                        ) > 0 && (
                          <TrendingUp className="absolute right-[-9px] top-[-11px] h-4 w-4 rounded-full bg-green-100 p-[2px] text-green-700" />
                        )}
                      {!isParent &&
                        user?.role &&
                        hasPreviousVersion(
                          row.original,
                          'stockQuantity',
                          user.role
                        ) &&
                        getDeltaSign(
                          row.original,
                          'stockQuantity',
                          user?.role
                        ) < 0 && (
                          <TrendingDown className="absolute right-[-9px] top-[-11px] h-4 w-4 rounded-full bg-red-100 p-[2px] text-red-700" />
                        )}
                    </TableCellDisplay>
                  </div>
                </TooltipTrigger>
                {!isParent &&
                  user?.role &&
                  hasPreviousVersion(
                    row.original,
                    'stockQuantity',
                    user.role
                  ) && (
                    <TooltipContent>
                      <p className="text-sm font-semibold">
                        {t('kpi.stock_modified')}
                      </p>
                      <p>
                        <span className="text-gray-500">
                          {t('general.previous')} :
                        </span>{' '}
                        <span>
                          {getMyLastValue(
                            row.original,
                            'stockQuantity',
                            user?.role
                          )}
                        </span>
                      </p>
                      <p>
                        <span className="text-gray-500">
                          {t('general.current')} :
                        </span>{' '}
                        <span>
                          {getCurrentValue(row.original, 'stockQuantity')}
                        </span>
                      </p>
                      <p className="font-semibold">
                        <span className="text-gray-500">
                          {t('general.gap')} :
                        </span>{' '}
                        <span
                          className={cn({
                            'text-red-700':
                              getDeltaValue(
                                row.original,
                                'stockQuantity',
                                user?.role
                              ) < 0,
                            'text-green-700':
                              getDeltaValue(
                                row.original,
                                'stockQuantity',
                                user?.role
                              ) > 0,
                          })}
                        >
                          {getDeltaValue(
                            row.original,
                            'stockQuantity',
                            user?.role
                          )}
                        </span>
                      </p>
                    </TooltipContent>
                  )}
              </Tooltip>
            )}
            {!isEditing &&
              !isParent &&
              displayValue === 900 &&
              row.original.activeVersion.shopifyVariantId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center px-1">
                      <InfoIcon className="h-4 w-4 text-gray-500" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-sm">{t('kpi.stock_oversell_warning')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
          </div>
          {kpiComparison === KPIComparison.STOCK && (
            <div
              className={cn(
                'min-w-[180px] text-right',
                !isOfferLocked && !isParent
                  ? 'cursor-pointer'
                  : 'cursor-not-allowed opacity-50'
              )}
              onClick={() =>
                !isParent &&
                handleCellClick(row.original.activeVersion.id, 'stockQuantity')
              }
            >
              <div className="flex h-full w-full items-center justify-end px-2 text-sm">
                <span>{!isParent ? `${getPreviousItem() || null}` : '—'}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefStockQuantity = ({
  handleBlur,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const kpiComparison = useAtomValue(kpiComparisonState);
  const { setColumnSort } = useSetColumnSort();

  const accessorKey: keyof DealItem = 'stockQuantity';
  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'StockQuantity',
      filterFn: rangeFilterFn,
      accessorFn: (row) => row.activeVersion?.stockQuantity ?? 0,
      aggregationFn: 'sum',
      sortingFn: 'basic',
      header: () => (
        <div className="flex w-full flex-row">
          <div className="flex flex-1 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('kpi.stock_tooltip')}</p>
              </TooltipContent>
            </Tooltip>{' '}
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.stock')}
              <SortIcon pointer="stockQuantity" />
            </div>
          </div>
          {kpiComparison === KPIComparison.STOCK && (
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.stock_compared')}
              <SortIcon pointer="stockQuantity" />
            </div>
          )}
        </div>
      ),
      cell: ({ row }) => (
        <StockQuantityCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: kpiComparison === KPIComparison.STOCK ? 360 : 180,
    }),
    [
      t,
      kpiComparison,
      setColumnSort,
      handleBlur,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
    ]
  );
};

export default useColumnDefStockQuantity;
